import React from 'react';

import { Close } from '@mui/icons-material';
import { OverlayDisplayDiv } from '@UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { CocoaExposureChart } from './CocoaDash/CocoaExposureChart';
import { ExposureCard } from '../ExposureCard';
import {
  getEntriesFiltered,
  getExposureCardData,
  getMaterialToAccountById,
  invReportsToStackedBar,
  reportsToSummaryMap,
} from '../dashboardUtils';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { useExposureStore } from '../../../components/Stores/exposureDataStore';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { IInventoryReportType } from '@sp-types/InventoryReportType';
import { StackedBarChartElement } from '@sp-types/dashboardTypes';

interface Props {
  close: () => void;
  materialId: string;
  orders: StackedBarChartElement[];
  inventory: IInventoryReportType[];
  positions: StackedBarChartElement[];
}
export const ExposureDetailsPopup: React.FC<Props> = ({ close, materialId, orders, inventory, positions }) => {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap, getCurrencyInUSD } = useCurrencyRates();
  const { netExposure, isNetExposureLoading } = useExposureStore();
  const { convertionMap } = useCurrencyRates();

  const [inventoryValues, setInventoryValues] = React.useState<number[]>([]);
  const [ordersValues, setOrdersValues] = React.useState<number[]>([]);
  const [positionsValues, setPositionsValues] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (inventory) {
      const materialReportsMap = reportsToSummaryMap(inventory, 0, materialsMap, pricesMap, false, getCurrencyInUSD);
      const materialsEntries = getEntriesFiltered(materialReportsMap, materialId);
      const barChartData = invReportsToStackedBar(materialsEntries, materialsMap, colorsMap);

      setInventoryValues(
        barChartData.find((a) => a.materialId === materialId)?.valueArray.map(({ value }) => value) || [],
      );
    }

    if (orders) {
      const flatOrders = [
        0,
        ...orders.filter((o) => o.materialId === materialId).flatMap((order) => order.valueArray.map((a) => a.value)),
      ];

      setOrdersValues(flatOrders);
    }

    if (positions) {
      setPositionsValues([
        0,
        ...positions
          .filter((p) => p.materialId === materialId)
          .flatMap((position) => position.valueArray.map((a) => a.value)),
      ]);
    }
  }, [inventory, orders, materialId, materialsMap, colorsMap]);

  return (
    <OverlayDisplayDiv setDisplayOverlay={close} hideClose>
      <div className="bg-white flex flex-col p-4 gap-4 min-w-96">
        <div className="flex flex-row justify-between">
          <h4>
            Detailed exposure of
            <span style={{ color: colorsMap[materialId] || '#000' }}> {materialsMap[materialId]?.name}</span>
          </h4>
          <Close className="cursor-pointer align-self-baseline text-purple me-1" onClick={close} />
        </div>
        <div className="flex">
          <ExposureCard
            {...getExposureCardData(
              netExposure,
              getMaterialToAccountById(account, materialId || 'general'),
              account.riskPercents,
              convertionMap,
              materialsMap,
              materialsMap[materialId],
            )}
            material={materialsMap[materialId]}
            isMiniView
          />
        </div>
        <div className="gap-2 flex flex-col">
          <div className="flex flex-col  shadow-custom p-3 gap-3 w-[50rem] h-[30rem]">
            <CocoaExposureChart
              material={materialId === 'RFVCLLEUDC1' ? 'mass' : materialId === 'RFVCBLEUDC1' ? 'butter' : 'other'}
              orders={ordersValues}
              positions={positionsValues}
              inventory={inventoryValues}
              hasRatio={!!materialsMap[materialId]?.hasRatio}
            />
          </div>
        </div>
      </div>
    </OverlayDisplayDiv>
  );
};
